import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Link } from '@ui/app-router';
import { Avatar, AvatarFallback, AvatarImage, Skeleton } from '@ui/primitives';
import { VirtualTable } from '@ui/primitives/table';
import { getAbbreviation } from '@ui/utils';
import { Info } from 'lucide-react';
import { DateTime } from 'luxon';
const STATUS_KEYWORDS = [
    {
        word: 'production',
        className: 'text-green-600 dark:text-green-400'
    },
    {
        word: 'failed',
        className: 'text-red-600 dark:text-red-400'
    },
    {
        word: 'deprecated',
        className: 'text-amber-600 dark:text-amber-400'
    }
];
function formatRelativeTime(isoDate) {
    var _DateTime_fromISO_toRelative;
    return (_DateTime_fromISO_toRelative = DateTime.fromISO(isoDate).toRelative()) !== null && _DateTime_fromISO_toRelative !== void 0 ? _DateTime_fromISO_toRelative : isoDate;
}
function highlightStatusWords(message) {
    const regex = new RegExp("\\b(".concat(STATUS_KEYWORDS.map((k)=>k.word).join('|'), ")\\b"), 'gi');
    const parts = message.split(regex);
    return parts.map((part, i)=>{
        const keyword = STATUS_KEYWORDS.find((k)=>part.toLowerCase() === k.word.toLowerCase());
        if (keyword) {
            return /*#__PURE__*/ _jsx("span", {
                className: keyword.className,
                children: part
            }, "".concat(keyword.word, "-").concat(i));
        }
        return part;
    });
}
function getLogData(log) {
    const data = log.action === 'delete' ? log.oldData : log.newData;
    return data;
}
const headers = [
    {
        dataKey: 'author',
        label: '',
        width: 56,
        align: 'left'
    },
    {
        dataKey: 'event',
        label: 'Event',
        width: 400,
        align: 'left'
    },
    {
        dataKey: 'time',
        label: 'Time',
        width: 120,
        align: 'right'
    }
];
export function AuditLogTable(param) {
    let { logs, isLoading, isFetchingNextPage, error, loadNextPage } = param;
    const rows = logs.map((log)=>{
        var _log_author_avatarColor, _logData_users, _logData_teams;
        const isFailure = log.result === 'failure';
        const logData = getLogData(log);
        return {
            author: isFailure ? /*#__PURE__*/ _jsx("div", {
                className: "flex h-10 w-10 items-center justify-center rounded-full bg-muted",
                children: /*#__PURE__*/ _jsx(Info, {
                    className: "h-5 w-5 text-muted-foreground",
                    "aria-hidden": true
                })
            }) : /*#__PURE__*/ _jsx(Link, {
                to: "/profile/$username",
                params: {
                    username: log.author.username
                },
                className: "shrink-0",
                children: /*#__PURE__*/ _jsxs(Avatar, {
                    className: "h-10 w-10",
                    children: [
                        log.author.portrait && /*#__PURE__*/ _jsx(AvatarImage, {
                            src: log.author.portrait,
                            alt: log.author.name
                        }),
                        /*#__PURE__*/ _jsx(AvatarFallback, {
                            style: {
                                backgroundColor: (_log_author_avatarColor = log.author.avatarColor) !== null && _log_author_avatarColor !== void 0 ? _log_author_avatarColor : '#6b7280'
                            },
                            children: getAbbreviation(log.author.name)
                        })
                    ]
                })
            }),
            event: /*#__PURE__*/ _jsxs("div", {
                className: "text-sm text-muted-foreground",
                children: [
                    !isFailure && /*#__PURE__*/ _jsx(Link, {
                        to: "/profile/$username",
                        params: {
                            username: log.author.username
                        },
                        className: "font-medium text-foreground hover:underline",
                        children: log.author.name
                    }),
                    !isFailure && ' ',
                    highlightStatusWords(log.message),
                    (logData === null || logData === void 0 ? void 0 : logData.name) && /*#__PURE__*/ _jsx("span", {
                        className: "ml-2 text-xs font-bold text-white",
                        children: logData.name
                    }),
                    log.type === 'collaborators' && (logData === null || logData === void 0 ? void 0 : logData.contributors) && /*#__PURE__*/ _jsx("span", {
                        className: "ml-2 text-xs font-bold text-white",
                        children: logData.contributors.map((c)=>c.name).join(', ')
                    }),
                    log.type === 'environment-members' && logData && /*#__PURE__*/ _jsx("span", {
                        className: "ml-2 text-xs font-bold text-white",
                        children: [
                            ...(_logData_users = logData.users) !== null && _logData_users !== void 0 ? _logData_users : [],
                            ...(_logData_teams = logData.teams) !== null && _logData_teams !== void 0 ? _logData_teams : []
                        ].map((m)=>m.name).join(', ')
                    }),
                    log.type === 'integration' && (logData === null || logData === void 0 ? void 0 : logData.integrationName) && /*#__PURE__*/ _jsxs("span", {
                        className: "ml-2 text-xs text-muted-foreground",
                        children: [
                            log.action === 'update' && /*#__PURE__*/ _jsx("span", {
                                className: "mr-1",
                                children: "to"
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                className: "font-bold text-white",
                                children: logData.integrationName
                            })
                        ]
                    })
                ]
            }),
            time: /*#__PURE__*/ _jsx("span", {
                className: "whitespace-nowrap text-xs text-muted-foreground",
                children: formatRelativeTime(log.createdAt)
            })
        };
    });
    if (error) {
        return /*#__PURE__*/ _jsx("div", {
            className: "rounded-lg border border-destructive bg-destructive/10 p-4",
            children: /*#__PURE__*/ _jsx("p", {
                className: "text-sm text-destructive",
                children: "Failed to load audit logs"
            })
        });
    }
    if (isLoading) {
        return /*#__PURE__*/ _jsx("div", {
            className: "divide-y divide-border",
            children: Array.from({
                length: 5
            }).map((_, i)=>/*#__PURE__*/ _jsxs("div", {
                    className: "flex items-center gap-3 py-3",
                    children: [
                        /*#__PURE__*/ _jsx(Skeleton, {
                            className: "h-10 w-10 shrink-0 rounded-full"
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            className: "min-w-0 flex-1 space-y-1",
                            children: /*#__PURE__*/ _jsx(Skeleton, {
                                className: "h-4 w-48"
                            })
                        }),
                        /*#__PURE__*/ _jsx(Skeleton, {
                            className: "h-4 w-20 shrink-0"
                        })
                    ]
                }, i))
        });
    }
    if (logs.length === 0) {
        return /*#__PURE__*/ _jsx("div", {
            className: "flex min-h-64 items-center justify-center rounded-lg border border-border bg-muted/50 p-8",
            children: /*#__PURE__*/ _jsx("p", {
                className: "text-sm text-muted-foreground",
                children: "No audit log entries found"
            })
        });
    }
    return /*#__PURE__*/ _jsx("div", {
        className: "overflow-hidden rounded-lg border border-border bg-card",
        children: /*#__PURE__*/ _jsx(VirtualTable, {
            loadNextPage: loadNextPage,
            rows: rows,
            headers: headers,
            height: 600,
            isLoading: isFetchingNextPage,
            defaultItemHeight: 64
        })
    });
}
